import { useState, useEffect } from 'react'
import { Table, Card, Button, Space, Modal, Form, Input, Select, message, Tag, Popconfirm } from 'antd'
import { PlusOutlined, SearchOutlined, ReloadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { supplierApi } from '../services/api'

const { Option } = Select

interface Supplier {
  id: number
  name: string
  contact: string
  phone: string
  address: string
  type: number
  status: number
  remark: string
  created_at: string
}

const typeMap: Record<number, { text: string; color: string }> = {
  1: { text: '原材料供应商', color: 'blue' },
  2: { text: '包装材料供应商', color: 'green' },
  3: { text: '设备供应商', color: 'orange' },
  4: { text: '其他', color: 'default' }
}

export default function Suppliers() {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<Supplier[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [modalVisible, setModalVisible] = useState(false)
  const [currentSupplier, setCurrentSupplier] = useState<Supplier | null>(null)
  const [form] = Form.useForm()
  const [searchForm] = Form.useForm()

  useEffect(() => {
    loadData()
  }, [page, pageSize])

  const loadData = async () => {
    setLoading(true)
    try {
      const values = searchForm.getFieldsValue()
      const res = await supplierApi.getList({
        page,
        page_size: pageSize,
        ...values
      })
      if (res.code === 200) {
        setData(res.data?.list || res.data || [])
        setTotal(res.data?.total || res.data?.length || 0)
      }
    } catch (err) {
      message.error('加载供应商列表失败')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    setPage(1)
    loadData()
  }

  const handleReset = () => {
    searchForm.resetFields()
    setPage(1)
    loadData()
  }

  const handleCreate = () => {
    form.resetFields()
    setCurrentSupplier(null)
    setModalVisible(true)
  }

  const handleEdit = (record: Supplier) => {
    setCurrentSupplier(record)
    form.setFieldsValue(record)
    setModalVisible(true)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      let res
      if (currentSupplier) {
        res = await supplierApi.update(currentSupplier.id, values)
      } else {
        res = await supplierApi.create(values)
      }
      if (res.code === 200) {
        message.success(currentSupplier ? '更新成功' : '创建成功')
        setModalVisible(false)
        loadData()
      } else {
        message.error(res.message || '操作失败')
      }
    } catch (err) {
      message.error('提交失败')
    }
  }

  const handleDelete = async (id: number) => {
    try {
      const res = await supplierApi.delete(id)
      if (res.code === 200) {
        message.success('删除成功')
        loadData()
      } else {
        message.error(res.message || '删除失败')
      }
    } catch (err) {
      message.error('删除失败')
    }
  }

  const handleToggleStatus = async (record: Supplier) => {
    try {
      const res = await supplierApi.update(record.id, { status: record.status === 1 ? 0 : 1 })
      if (res.code === 200) {
        message.success(record.status === 1 ? '已停用' : '已启用')
        loadData()
      } else {
        message.error(res.message || '操作失败')
      }
    } catch (err) {
      message.error('操作失败')
    }
  }

  const columns = [
    { title: '供应商名称', dataIndex: 'name', key: 'name', width: 150 },
    { title: '联系人', dataIndex: 'contact', key: 'contact', width: 100 },
    { title: '联系电话', dataIndex: 'phone', key: 'phone', width: 120 },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (v: number) => <Tag color={typeMap[v]?.color}>{typeMap[v]?.text}</Tag>
    },
    { title: '地址', dataIndex: 'address', key: 'address', width: 200, ellipsis: true },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (v: number) => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '正常' : '停用'}</Tag>
    },
    { title: '备注', dataIndex: 'remark', key: 'remark', width: 150, ellipsis: true },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: any, record: Supplier) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Button type="link" size="small" onClick={() => handleToggleStatus(record)}>
            {record.status === 1 ? '停用' : '启用'}
          </Button>
          <Popconfirm title="确认删除该供应商?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Form form={searchForm} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="keyword">
            <Input placeholder="供应商名称/联系人" style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="type">
            <Select placeholder="类型" style={{ width: 150 }} allowClear>
              {Object.entries(typeMap).map(([k, v]) => <Option key={k} value={Number(k)}>{v.text}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="状态" style={{ width: 100 }} allowClear>
              <Option value={1}>正常</Option>
              <Option value={0}>停用</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新增供应商</Button>
            </Space>
          </Form.Item>
        </Form>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) }
          }}
        />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={currentSupplier ? '编辑供应商' : '新增供应商'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form form={form} labelCol={{ span: 5 }} wrapperCol={{ span: 18 }}>
          <Form.Item name="name" label="供应商名称" rules={[{ required: true, message: '请输入供应商名称' }]}>
            <Input placeholder="请输入供应商名称" />
          </Form.Item>
          <Form.Item name="contact" label="联系人">
            <Input placeholder="请输入联系人姓名" />
          </Form.Item>
          <Form.Item name="phone" label="联系电话">
            <Input placeholder="请输入联系电话" />
          </Form.Item>
          <Form.Item name="type" label="类型" rules={[{ required: true, message: '请选择类型' }]}>
            <Select placeholder="请选择供应商类型">
              {Object.entries(typeMap).map(([k, v]) => <Option key={k} value={Number(k)}>{v.text}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="address" label="地址">
            <Input.TextArea rows={2} placeholder="请输入供应商地址" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} placeholder="请输入备注信息" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
